import React from "react";
import { useFormContext } from "react-hook-form";
import {
    ContentSection,
    DropdownOption,
    FormCheckbox,
    FormDatePicker,
    FormDropdown,
    FormFields,
    FormTextField,
    removeTextQuotes,
    TextField
} from "sprint-common-web";

import { Originator } from "../../../../../common/data/lookup-data-providers/OriginatorsDataProvider";
import { RecordType } from "../../../../../common/types/RecordType";
import { StorageType } from "../../../../../common/types/StorageType";
import { mapEnumToDropdownOptions } from "../../../../../common/utils/mapEnumToDropdownOptions";
import { validateFileExtension } from "../../../../../common/utils/validateFileExtension";
import { filePathValidationRulePattern } from "../../../../../common/utils/validation-rules/filePath";
import { useCreateFileRecordState } from "../hooks/state/useCreateFileRecordState";
import { useCreateFileRecordFormOptions } from "../hooks/useCreateFileRecordFormOptions";

export const CreateFileRecordDeliveryInfoForm: React.FC = () => {
    const { setSelectedRecordType, isLoading } = useCreateFileRecordState();

    const { originators, areOriginatorsLoading } = useCreateFileRecordFormOptions();

    const { control, errors, watch } = useFormContext();

    const storageType = watch("storageType");
    const recordType = watch("recordType");

    const mapOriginatorsToDropdownOptions = (allOriginators: Originator[]): DropdownOption[] =>
        allOriginators.map((originator) => ({
            key: originator.id,
            text: originator.name
        }));

    return (
        <ContentSection
            header="Delivery info"
            isLoading={areOriginatorsLoading || isLoading}
            style={{ minHeight: "80vh" }}
        >
            <FormFields>
                <FormDropdown
                    label={"Record type"}
                    name={"recordType"}
                    control={control}
                    errors={errors}
                    options={mapEnumToDropdownOptions(RecordType)}
                    rules={{
                        required: "Select asset record type"
                    }}
                    onChange={(value) => {
                        setSelectedRecordType(Number(value));
                    }}
                />
                <FormTextField
                    label="Delivery file name"
                    name={"fileName"}
                    control={control}
                    errors={errors}
                    rules={{
                        required: "Enter delivery file name",
                        validate: (filename) => validateFileExtension(filename, recordType)
                    }}
                />
                <FormDatePicker
                    label="Arrival date"
                    name={"arrivalDate"}
                    control={control}
                    errors={errors}
                    rules={{
                        required: "Enter arrival date"
                    }}
                />
                <FormDropdown
                    label={"Originator"}
                    name={"originator"}
                    control={control}
                    errors={errors}
                    testId="file-record-originator-dropdown"
                    options={mapOriginatorsToDropdownOptions(originators)}
                    rules={{
                        required: "Select originator"
                    }}
                />
                <FormTextField label="Reference" name={"reference"} control={control} errors={errors} />

                <FormTextField
                    label="File path"
                    name={"path"}
                    control={control}
                    errors={errors}
                    onInputChange={(value) => {
                        const filePath = removeTextQuotes(value);
                        control.setValue("path", filePath);
                        control.setValue("storageType", !filePath ? StorageType.Cloud : StorageType.OnPrem);
                    }}
                    rules={{
                        pattern: filePathValidationRulePattern
                    }}
                />
                <TextField
                    disabled
                    label="Storage type"
                    name={"storageType"}
                    value={storageType === StorageType.Cloud ? "Cloud" : "OnPrem"}
                    data-testid="file-record-storage-type-input"
                />
                <FormCheckbox
                    styles={{ root: { paddingTop: 10 } }}
                    label={"Approve buzz notification"}
                    control={control}
                    name={"isNotificationApproved"}
                    data-testId="file-record-approve-notifications"
                />
            </FormFields>
        </ContentSection>
    );
};
